import { z } from 'zod';

import { MoneySchema } from '@/shared/lib/money';

const BIRTH_DATE = /^(0[1-9]|[12]\d|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/;

export const ViewerSchema = z.object({
  authenticated: z.boolean(),
  firstName: z.string().nullable(),
});
export type Viewer = z.infer<typeof ViewerSchema>;

export const ProfileSchema = z.object({
  id: z.string(),
  phone: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  birthDate: z.string().nullable(),
  email: z.string().nullable(),
  phoneVerified: z.boolean(),
  emailVerified: z.boolean(),
});
export type Profile = z.infer<typeof ProfileSchema>;

export const UpdateProfileSchema = z
  .object({
    firstName: z.string().trim().min(1, 'Укажите имя').max(60),
    lastName: z.string().trim().min(1, 'Укажите фамилию').max(60),
    birthDate: z
      .string()
      .trim()
      .refine((value) => value === '' || BIRTH_DATE.test(value), 'Дата в формате ДД.ММ.ГГГГ'),
  })
  .strict();
export type UpdateProfileCommand = z.infer<typeof UpdateProfileSchema>;

export const AddressSchema = z.object({
  id: z.string(),
  kind: z.enum(['courier', 'pickup']),
  city: z.string(),
  line: z.string(),
  meta: z.string().nullable(),
  recipient: z.string(),
  phone: z.string(),
  isDefault: z.boolean(),
});
export type Address = z.infer<typeof AddressSchema>;
export const AddressListSchema = z.array(AddressSchema);

export const AddAddressSchema = z
  .object({
    city: z.string().trim().min(2, 'Укажите город').max(80),
    line: z.string().trim().min(5, 'Укажите улицу, дом и квартиру').max(200),
  })
  .strict();
export type AddAddressCommand = z.infer<typeof AddAddressSchema>;

export const LoyaltyLevelSchema = z.object({
  id: z.string(),
  name: z.string(),
  from: z.string(),
  perk: z.string(),
  status: z.enum(['passed', 'current', 'next']),
});

export const LoyaltySchema = z.object({
  levelName: z.string(),
  ratePercent: z.number().int().min(0).max(100),
  balance: z.number().int().min(0),
  spent: MoneySchema,
  next: z
    .object({ name: z.string(), threshold: MoneySchema, remaining: MoneySchema, perk: z.string() })
    .nullable(),
  progress: z.number().min(0).max(100),
  levels: z.array(LoyaltyLevelSchema),
});
export type Loyalty = z.infer<typeof LoyaltySchema>;

export const BonusEntrySchema = z.object({
  id: z.string(),
  title: z.string(),
  date: z.string(),
  amount: z.number().int(),
  status: z.enum(['pending', 'accrued', 'spent']),
});
export type BonusEntry = z.infer<typeof BonusEntrySchema>;

export const SendOtpSchema = z
  .object({
    phone: z.string().regex(/^9\d{9}$/),
    consent: z.literal(true),
  })
  .strict();
export const SendOtpResultSchema = z.object({ resendAfterSeconds: z.number().int().min(0) });

export const VerifyOtpSchema = z
  .object({
    phone: z.string().regex(/^9\d{9}$/),
    code: z.string().regex(/^\d{4}$/),
  })
  .strict();

export const OAuthLoginSchema = z
  .object({ provider: z.enum(['t-id', 'yandex-id']), consent: z.literal(true) })
  .strict();

export const NewsletterSchema = z
  .object({ email: z.email().max(120), consent: z.literal(true) })
  .strict();
